import { ScrollItem } from "./ScrollItem";
import { ScrollItemVO } from "./ScrollItemVO";
import { ScrollItemEvent } from "./ScrollItemEvent";

import MovieClip = createjs.MovieClip;
import EventDispatcher = createjs.EventDispatcher;
import MouseEvent = createjs.MouseEvent;

/**
 *
 * Component:    ItemList
 *
 * This component is based on a fixed number of visible "container"-items (ScrollItem).
 *
 * @param disp
 *         The disp-parameter should be a MovieClip-Container "mc_itemContainer" with the following childs:
 *         mc_itemContainer.item0
 *         mc_itemContainer.item1
 *         ..
 *         mc_itemContainer.item[itemsVisibleAtOnce]
 *
 *      mc_itemContainer.txt_index
 *
 * Dispatches ScrollItemEvent.CLICK
 *
 */

export class ItemList extends EventDispatcher {
    protected _componentDisp: MovieClip;

    protected _itemsVisibleAtOnce: number = 0;
    protected _itemCount: number = 0;
    protected _currentStartIndex: number = 0;

    public scrollInstanceName: string = "item";

    protected _itemVEList: ScrollItem[];
    protected _itemVOList: ScrollItemVO[];
    protected _idCounter: number = 0;

    constructor(disp: MovieClip) {
        super();

        this._componentDisp = disp;
        this._idCounter = 0;
        this._componentDisp.addEventListener("click", this.bindFunction(this.onClick));
    }

    public get componentDisp(): MovieClip {
        return this._componentDisp;
    }

    public set scrollItemClass(value: any) {
        if (!this._itemVEList) {
            this._itemVEList = [];
        }
        let i: number = 0;

        while (this._componentDisp[this.scrollInstanceName + i]) {
            this._itemVEList.push(new value(this._componentDisp[this.scrollInstanceName + i]));
            i++;
        }
        this._itemsVisibleAtOnce = this._itemVEList.length;
    }

    public pushContent(scrollItemContent: ScrollItemVO): void {
        if (!this._itemVOList) {
            this._itemVOList = [];
        }
        scrollItemContent.id = this._idCounter++;
        this._itemVOList.push(scrollItemContent);
        this._itemCount = this._itemVOList.length;
    }

    public removeContent(scrollItemContent: ScrollItemVO): void {
        if (!this._itemVOList) {
            return;
        }
        this._itemVOList.splice(this._itemVOList.indexOf(scrollItemContent), 1);
        this._itemCount = this._itemVOList.length;
    }

    public initList(startItem: number = 0, allowEmptyPlacesAtTheEnd: boolean = false): void {
        this.updateItemList();
        this.updateActiveStates();
    }

    protected updateItemList(startItem: number = 0): void {
        this._currentStartIndex = startItem;

        for (let i: number = 0; i < this._itemsVisibleAtOnce; i++) {
            let itemVE: ScrollItem = this._itemVEList[i];
            let offsetIndex: number = i + startItem;

            if (offsetIndex < this._itemCount) {
                itemVE.fillWithContent(this._itemVOList[offsetIndex]);
                itemVE.show();
                itemVE.addEventListener(ScrollItemEvent.CLICK, this.bindFunction(this.onMouseClickOnItem));
                itemVE.addEventListener(ScrollItemEvent.ROLL_OVER, this.bindFunction(this.onMouseRollOverItem));
                itemVE.addEventListener(ScrollItemEvent.ROLL_OUT, this.bindFunction(this.onMouseRollOutItem));
            }
            else {
                itemVE.hide();
                itemVE.removeEventListener(ScrollItemEvent.CLICK, this.bindFunction(this.onMouseClickOnItem));
                itemVE.removeEventListener(ScrollItemEvent.ROLL_OVER, this.bindFunction(this.onMouseRollOverItem));
                itemVE.removeEventListener(ScrollItemEvent.ROLL_OUT, this.bindFunction(this.onMouseRollOutItem));
            }
        }
    }

    protected onMouseClickOnItem(event: ScrollItemEvent): void {
        this.dispatchEvent(new ScrollItemEvent(ScrollItemEvent.CLICK, event.scrollItem, event.originTarget));
    }

    private onMouseRollOverItem(event: ScrollItemEvent): void {
        this.dispatchEvent(new ScrollItemEvent(ScrollItemEvent.ROLL_OVER, event.scrollItem, event.originTarget));
    }

    private onMouseRollOutItem(event: ScrollItemEvent): void {
        this.dispatchEvent(new ScrollItemEvent(ScrollItemEvent.ROLL_OUT, event.scrollItem, event.originTarget));
    }

    public setActiveItemVO(activeItemId: number, activeState: boolean, multipleChoice: boolean = false): void {
        if (activeItemId > this._itemVOList.length) {
            return;
        }

        if (multipleChoice) {
            this._itemVOList[activeItemId].active = activeState;
        }
        else {
            for (let i: number = 0; i < this._itemVOList.length; i++) {
                let itemVO: ScrollItemVO = this._itemVOList[i];
                itemVO.active = activeItemId == itemVO.id;
            }
        }
        this.updateActiveStates();
    }

    public isItemActive(itemId: number): boolean {
        return this._itemVOList[itemId] && this._itemVOList[itemId].active;

    }

    protected updateActiveStates(): void {
        for (let i: number = 0; i < this._itemVEList.length; i++) {
            let itemVE: ScrollItem = this._itemVEList[i];

            if (itemVE && itemVE.scrollItemVO) {
                itemVE.isActive = this._itemVEList[i].scrollItemVO.active;
            }
        }
    }

    protected onClick(event: MouseEvent): void {
        switch (event.target) {
            case (<any>this._componentDisp).btn_up:
                this.naviUp();
                break;
            case (<any>this._componentDisp).btn_down:
                this.naviDown();
                break;
        }
    }

    public remove(): void {
        for (let i: number = 0; i < this._itemsVisibleAtOnce; i++) {
            this._itemVEList[i].remove();
        }

        this._componentDisp.removeEventListener("click", this.bindFunction(this.onClick));
    }

    public resetItems(): void {
        for (let i: number = 0; i < this._itemsVisibleAtOnce; i++) {
            let itemVE: ScrollItem = this._itemVEList[i];
            itemVE.reset();
        }
    }

    public clear(): void {
        this._itemVOList = [];
        this._itemCount = 0;
        this._idCounter = 0;

        for (let i: number = 0; i < this._itemsVisibleAtOnce; i++) {
            let itemVE: ScrollItem = this._itemVEList[i];
            itemVE.hide();
        }
    }

    public get itemsVisibleAtOnce(): number {
        return this._itemsVisibleAtOnce;
    }

    public get currentStartIndex(): number {
        return this._currentStartIndex;
    }

    public get voList(): ScrollItemVO[] {
        return this._itemVOList;
    }

    public get veList(): ScrollItem[] {
        return this._itemVEList;
    }

    public updateNavi(): void {
        // override me
    }

    protected naviUp(): void {
        // override me
    }

    protected naviDown(): void {
        // override me
    }
}

